// The header switch for privacy mode (task 402.10, owner M3), with Ctrl/⌘ + Shift + P, and the banner that keeps
// the mode visible while it is on — an operator must never wonder whether what they are reading is the real value.

import React from 'react';
import { Tooltip } from 'antd';
import { Eye, EyeOff } from 'lucide-react';
import { usePrivacyStore } from '../../stores/privacyStore';
import { PRIVACY_SHORTCUT } from './usePrivacyShortcut';

const EXPLANATION =
  'Shortens names, e-mail addresses, phone numbers and account numbers on screen, for sharing your screen. ' +
  'It changes what is drawn, not what you are allowed to see, and it is remembered in this browser only.';

export default function PrivacyToggle({ className }: { className?: string }): React.ReactElement {
  const enabled = usePrivacyStore((s) => s.enabled);
  const toggle = usePrivacyStore((s) => s.toggle);
  const Icon = enabled ? EyeOff : Eye;

  return (
    <Tooltip title={`${enabled ? 'Privacy mode is on. ' : ''}${EXPLANATION} (${PRIVACY_SHORTCUT})`}>
      <button
        type="button"
        className={className}
        onClick={toggle}
        data-testid="privacy-toggle"
        data-enabled={enabled ? 'true' : 'false'}
        aria-pressed={enabled}
        aria-label={
          enabled
            ? 'Privacy mode on — click to show values'
            : 'Privacy mode off — click to hide values'
        }
      >
        <Icon size={18} />
      </button>
    </Tooltip>
  );
}

/** A quiet strip under the header while the mode is on. Rendered by the shell, above the page. */
export function PrivacyBanner(): React.ReactElement | null {
  const enabled = usePrivacyStore((s) => s.enabled);
  const toggle = usePrivacyStore((s) => s.toggle);
  if (!enabled) return null;

  return (
    <div
      data-testid="privacy-banner"
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 16px',
        fontSize: 12,
        background: 'rgba(250, 173, 20, 0.12)',
        borderBottom: '1px solid rgba(250, 173, 20, 0.35)',
      }}
    >
      <EyeOff size={13} aria-hidden="true" />
      <span>
        <strong>Privacy mode is on.</strong> Names, e-mail addresses, phone numbers and account
        numbers are shortened on screen. This changes what is drawn, not what you may see.
      </span>
      <button
        type="button"
        onClick={toggle}
        style={{
          marginLeft: 'auto',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        Turn off
      </button>
    </div>
  );
}
